import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import MuiLink from "@mui/material/Link";
import RouterLink from "arena/components/Link";
import Box from "@mui/material/Box";
import { useArena } from "core/components/ArenaProvider";

const StyledChip = styled(Chip)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";

  // Фиксированные цвета фона без наследования
  let chipBackground;
  let textColor;

  if (isDark) {
    // Для темной темы - фиксированный серый цвет
    chipBackground = "#333333";
    textColor = theme.palette.text.primary;
  } else {
    // Для светлой темы - светло-серый цвет
    chipBackground = "#f0f0f0";
    textColor = theme.palette.text.primary;
  }

  return {
    minHeight: 44, // фиксированная минимальная высота
    height: "auto",
    padding: theme.spacing(0.75, 1.5), // как на скриншоте
    borderRadius: 20, // более округлые как на скриншоте
    backgroundColor: chipBackground,
    border: "none",
    display: "flex",
    alignItems: "center", // выравниваем содержимое по центру
    [theme.breakpoints.down("sm")]: {
      minHeight: 36, // меньше на мобилке
      padding: theme.spacing(0.5, 1), // меньше отступы на мобилке
    },
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      fontWeight: 500,
      color: textColor,
      fontSize: "0.875rem",
      [theme.breakpoints.down("sm")]: {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
        fontSize: "0.8rem", // меньший шрифт на мобилке
      },
    },
    "& .MuiChip-avatar": {
      width: 28,
      height: 28,
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5), // отступ от картинки до текста
      [theme.breakpoints.down("sm")]: {
        width: 24,
        height: 24,
        marginLeft: theme.spacing(0.25),
        marginRight: theme.spacing(0.25), // меньше отступ на мобилке
      },
    },
  };
});

const ChipsContainer = styled(Box)(({ theme }) => ({
  // padding: theme.spacing(1, 1, 1),
  // [theme.breakpoints.up("sm")]: {
  //   padding: theme.spacing(1, 1, 1),
  // },
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1.5), // чуть больше отступ между чипами
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 1),
  },
}));

export default function ChipsList({ items }) {
  const arena = useArena();

  // Функция для определения внутренней ссылки
  const isInternalLink = (url, openInNewWindow) => {
    if (openInNewWindow) return false;
    if (!url) return false;

    // Относительные ссылки
    if (url.startsWith("/")) return true;

    // Проверяем домен арены
    try {
      const linkUrl = new URL(url);
      const arenaUrl = new URL(arena.url);
      return linkUrl.hostname === arenaUrl.hostname;
    } catch {
      // Если не удалось распарсить URL, считаем внутренней
      return true;
    }
  };

  return (
    <ChipsContainer>
      {items.map((item, index) => {
        const getIconUrl = (thumbnails) => {
          if (!thumbnails || !thumbnails.length) return null;

          // Сначала ищем X128
          const x128Thumbnail = thumbnails.find(
            (thumb) => thumb.format === "X128"
          );
          if (x128Thumbnail) return x128Thumbnail.url;

          // Если x128 нет, берем максимальный по ширине
          const maxWidthThumbnail = thumbnails.reduce((max, current) => {
            return (current.width || 0) > (max.width || 0) ? current : max;
          }, thumbnails[0]);

          return maxWidthThumbnail?.url;
        };

        const iconUrl = getIconUrl(item.icon?.thumbnails);

        const chipProps = {
          key: index,
          label: item.title,
          variant: "filled",
          clickable: true,
          ...(iconUrl && {
            avatar: <Avatar src={iconUrl} alt={item.title} />,
          }),
        };

        const isInternal = isInternalLink(item.url, item.openInNewWindow);

        if (item.openInNewWindow) {
          // Внешняя ссылка в новом окне
          return (
            <MuiLink
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              key={index}
            >
              <StyledChip {...chipProps} />
            </MuiLink>
          );
        }

        if (isInternal) {
          // Внутренняя ссылка через роутер
          const linkPath = item.url.startsWith("/")
            ? item.url
            : new URL(item.url).pathname;
          return (
            <RouterLink
              to={linkPath}
              key={index}
              style={{ textDecoration: "none" }}
            >
              <StyledChip {...chipProps} />
            </RouterLink>
          );
        }

        // Внешняя ссылка в том же окне
        return (
          <MuiLink href={item.url} underline="none" key={index}>
            <StyledChip {...chipProps} />
          </MuiLink>
        );
      })}
    </ChipsContainer>
  );
}

ChipsList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      icon: PropTypes.shape({
        thumbnails: PropTypes.arrayOf(
          PropTypes.shape({
            format: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
          })
        ),
      }),
      openInNewWindow: PropTypes.bool,
    })
  ).isRequired,
};
